import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  Slider,
  Switch,
  FormControlLabel,
  TextField,
  Grid,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Card,
  CardContent,
  Snackbar,
  Alert,
  useTheme
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';

const SettingsView = () => {
  const theme = useTheme();
  
  // UI Settings
  const [darkMode, setDarkMode] = useState(false);
  const [displayMode, setDisplayMode] = useState('standard');
  const [dateFormat, setDateFormat] = useState('MM/dd/yyyy');
  
  // Threshold Settings
  const [threshold, setThreshold] = useState(66);
  const [autoThreshold, setAutoThreshold] = useState(false);
  const [thresholdMethod, setThresholdMethod] = useState('percentile');
  
  // Data Settings
  const [dataRefreshRate, setDataRefreshRate] = useState(15);
  const [storageLimit, setStorageLimit] = useState(90);
  const [aggregationType, setAggregationType] = useState('average');
  
  // Alert Settings
  const [emailAlerts, setEmailAlerts] = useState(true);
  const [alertThreshold, setAlertThreshold] = useState(14);
  const [alertEmail, setAlertEmail] = useState('');
  
  // Notification state
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  
  const handleSaveSettings = () => {
    // In a real application, this would save settings to backend/localStorage
    setNotification({
      open: true,
      message: 'Settings saved successfully',
      severity: 'success'
    });
  };
  
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };
  
  const handleResetDefaults = () => {
    // Reset settings to defaults
    setThreshold(66);
    setAutoThreshold(false);
    setThresholdMethod('percentile');
    setDataRefreshRate(15);
    setStorageLimit(90);
    setAggregationType('average');
    setEmailAlerts(true);
    setAlertThreshold(14);
    setDarkMode(false);
    setDisplayMode('standard');
    setDateFormat('MM/dd/yyyy');
    
    setNotification({
      open: true,
      message: 'Settings reset to defaults',
      severity: 'info'
    });
  };
  
  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Settings
        </Typography>
        
        <Box>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveSettings}
            sx={{ mr: 1 }}
          >
            Save Settings
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<RestoreIcon />}
            onClick={handleResetDefaults}
          >
            Reset to Defaults
          </Button>
        </Box>
      </Box>
      
      <Grid container spacing={3}>
        {/* Threshold Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Threshold Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={autoThreshold}
                    onChange={(e) => setAutoThreshold(e.target.checked)}
                  />
                }
                label="Auto-detect threshold"
                sx={{ mb: 2 }}
              />
              
              {autoThreshold ? (
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="threshold-method-label">Threshold Method</InputLabel>
                  <Select
                    labelId="threshold-method-label"
                    id="threshold-method"
                    value={thresholdMethod}
                    label="Threshold Method"
                    onChange={(e) => setThresholdMethod(e.target.value)}
                  >
                    <MenuItem value="percentile">Percentile (25th)</MenuItem>
                    <MenuItem value="average">Average of Clean/Dirty</MenuItem>
                    <MenuItem value="historical">Historical Analysis</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <Box sx={{ mb: 2 }}>
                  <Typography id="threshold-slider" gutterBottom>
                    Light Level Threshold: {threshold}
                  </Typography>
                  <Slider
                    value={threshold}
                    onChange={(e, newValue) => setThreshold(newValue)}
                    aria-labelledby="threshold-slider"
                    valueLabelDisplay="auto"
                    min={10}
                    max={150}
                  />
                  <Typography variant="body2" color="textSecondary">
                    Sensor readings below this value indicate cleaning is needed
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Data Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Data Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography id="refresh-slider" gutterBottom>
                  Data Refresh Rate: {dataRefreshRate} minutes
                </Typography>
                <Slider
                  value={dataRefreshRate}
                  onChange={(e, newValue) => setDataRefreshRate(newValue)}
                  aria-labelledby="refresh-slider"
                  valueLabelDisplay="auto"
                  min={5}
                  max={60}
                  step={5}
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography id="storage-slider" gutterBottom>
                  Data Storage Limit: {storageLimit} days
                </Typography>
                <Slider
                  value={storageLimit}
                  onChange={(e, newValue) => setStorageLimit(newValue)}
                  aria-labelledby="storage-slider"
                  valueLabelDisplay="auto"
                  min={30}
                  max={365}
                  step={30}
                />
              </Box>
              
              <FormControl fullWidth>
                <InputLabel id="aggregation-type-label">Aggregation Type</InputLabel>
                <Select
                  labelId="aggregation-type-label"
                  id="aggregation-type"
                  value={aggregationType}
                  label="Aggregation Type"
                  onChange={(e) => setAggregationType(e.target.value)}
                >
                  <MenuItem value="average">Average</MenuItem>
                  <MenuItem value="median">Median</MenuItem>
                  <MenuItem value="min">Minimum</MenuItem>
                  <MenuItem value="max">Maximum</MenuItem>
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>
        
        {/* UI Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                UI Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={darkMode}
                    onChange={(e) => setDarkMode(e.target.checked)}
                  />
                }
                label="Dark Mode"
                sx={{ mb: 2 }}
              />
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="display-mode-label">Display Mode</InputLabel>
                <Select
                  labelId="display-mode-label"
                  id="display-mode"
                  value={displayMode}
                  label="Display Mode"
                  onChange={(e) => setDisplayMode(e.target.value)}
                >
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="compact">Compact</MenuItem>
                  <MenuItem value="expanded">Expanded</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl fullWidth>
                <InputLabel id="date-format-label">Date Format</InputLabel>
                <Select
                  labelId="date-format-label"
                  id="date-format"
                  value={dateFormat}
                  label="Date Format"
                  onChange={(e) => setDateFormat(e.target.value)}
                >
                  <MenuItem value="MM/dd/yyyy">MM/DD/YYYY</MenuItem>
                  <MenuItem value="dd/MM/yyyy">DD/MM/YYYY</MenuItem>
                  <MenuItem value="yyyy-MM-dd">YYYY-MM-DD</MenuItem>
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Alert Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Alert Settings
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={emailAlerts}
                    onChange={(e) => setEmailAlerts(e.target.checked)}
                  />
                }
                label="Email Alerts"
                sx={{ mb: 2 }}
              />
              
              {emailAlerts && (
                <TextField
                  fullWidth
                  label="Alert Email"
                  variant="outlined"
                  value={alertEmail}
                  onChange={(e) => setAlertEmail(e.target.value)}
                  sx={{ mb: 2 }}
                />
              )}
              
              <Box>
                <Typography id="alert-threshold-slider" gutterBottom>
                  Alert Threshold: {alertThreshold} days before predicted wash
                </Typography>
                <Slider
                  value={alertThreshold}
                  onChange={(e, newValue) => setAlertThreshold(newValue)}
                  aria-labelledby="alert-threshold-slider"
                  valueLabelDisplay="auto"
                  min={1}
                  max={30}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Data Import/Export */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Data Import/Export
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => {/* Export data functionality */}}
                >
                  Export Data
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => {/* Import data functionality */}}
                >
                  Import Data
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseNotification} severity={notification.severity}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SettingsView;